<p-toast></p-toast>

<app-page-shell>
  <app-page-hero eyebrow="Governed HR Reporting" title="Annual HR Management Report"
    subtitle="Monthly workforce, Saudization, attendance, payroll, employee-relations, and hiring measures for companies and company groups.">
    <div actions>
      <button pButton type="button" label="Generate Month" icon="fa-solid fa-rotate"
        [disabled]="!canManage" (click)="activeDialog = 'generate'"></button>
      <button pButton type="button" label="Scorecards" icon="fa-solid fa-gauge-high"
        severity="secondary" [outlined]="true" [disabled]="!canManage" (click)="openScorecards()"></button>
      <button pButton type="button" label="Export Excel" icon="fa-solid fa-file-excel"
        severity="secondary" [outlined]="true" [disabled]="!canExport" (click)="exportWorkbook()"></button>
      <button pButton type="button" icon="fa-solid fa-refresh" severity="secondary"
        [outlined]="true" [loading]="loading" (click)="load()"></button>
    </div>
  </app-page-hero>

  <section class="report-toolbar">
    <p-inputGroup>
      <p-inputGroupAddon><i class="fa-solid fa-calendar"></i></p-inputGroupAddon>
      <p-iftalabel>
        <p-select inputId="annualReportYear" [options]="yearOptions" optionLabel="label" optionValue="value"
          [(ngModel)]="year" (onChange)="load()"></p-select>
        <label for="annualReportYear">Report year</label>
      </p-iftalabel>
    </p-inputGroup>

    <app-dashboard-company-scope-selector *ngIf="companyGroupId"
      label="Report company" [companyGroupName]="activeGroupName" [memberCompanies]="groupCompanies"
      [selectedCompanyId]="selectedGroupCompanyId"
      (selectedCompanyIdChange)="selectGroupCompany($event)">
    </app-dashboard-company-scope-selector>

    <div class="report-legend">
      <span><i class="status-dot green"></i> Healthy</span>
      <span><i class="status-dot amber"></i> Attention</span>
      <span><i class="status-dot red"></i> Critical</span>
      <span><i class="status-dot missing"></i> Not available</span>
    </div>
  </section>

  <section class="period-strip" *ngIf="periodRows.length">
    <article class="period-card" *ngFor="let period of periodRows">
      <div>
        <strong>{{ companyName(period.owningCompanyId) }}</strong>
        <span>{{ monthNames[(period.reportMonth || 1) - 1] }} {{ period.reportYear }}</span>
      </div>
      <p-tag [value]="period.periodStatus" [severity]="statusSeverity(period.periodStatus)"></p-tag>
      <div class="period-actions" *ngIf="canLock">
        <button pButton type="button" icon="fa-solid fa-check" [text]="true"
          title="Review" [disabled]="period.periodStatus !== 'DRAFT'" (click)="review(period)"></button>
        <button pButton type="button" icon="fa-solid fa-lock" [text]="true"
          title="Lock" [disabled]="period.periodStatus !== 'REVIEWED'" (click)="lock(period)"></button>
        <button pButton type="button" icon="fa-solid fa-lock-open" [text]="true"
          title="Reopen" [disabled]="period.periodStatus !== 'LOCKED'" (click)="openReopen(period)"></button>
      </div>
    </article>
  </section>

  <section class="empty-report" *ngIf="!loading && !reportSections.length">
    <i class="fa-solid fa-chart-column"></i>
    <h2>No annual report snapshots yet</h2>
    <p>Generate a reporting month to create governed metrics for the selected scope.</p>
  </section>

  <details class="company-report" *ngFor="let company of reportSections" [open]="true">
    <summary>
      <span class="company-icon"><i [class]="company.consolidated ? 'fa-solid fa-building-circle-check' : 'fa-solid fa-building'"></i></span>
      <span>
        <strong>{{ company.companyName }}</strong>
        <small>{{ company.consolidated ? 'Company-group consolidation' : 'Legal entity report' }}</small>
      </span>
      <p-tag *ngIf="company.consolidated" value="CONSOLIDATED" severity="info"></p-tag>
    </summary>

    <div class="matrix-scroll">
      <table class="annual-matrix">
        <thead>
          <tr>
            <th>Metric</th>
            <th *ngFor="let month of monthNames">{{ month }}</th>
            <th>Trend</th>
          </tr>
        </thead>
        <tbody *ngFor="let group of groupedMetrics(company)">
          <tr class="category-row" [attr.data-category]="group.code">
            <td colspan="14">{{ group.label }}</td>
          </tr>
          <tr *ngFor="let metric of group.metrics">
            <th class="metric-name">
              {{ metric.metricName }}
              <small>{{ metric.metricUnit }}</small>
            </th>
            <td *ngFor="let month of metric.months"
              class="metric-cell"
              [class.clickable]="!company.consolidated && month.periodStatus !== 'NOT_GENERATED'"
              [attr.data-status]="month.scorecardStatus"
              [attr.data-quality]="month.dataQualityStatus"
              (click)="openMetric(company, metric, month)">
              <span>{{ month.displayValue || '—' }}</span>
              <i *ngIf="month.periodStatus === 'LOCKED'" class="fa-solid fa-lock lock-mark"></i>
              <i *ngIf="month.overridden" class="fa-solid fa-pen override-mark"></i>
              <small *ngIf="month.dataQualityStatus === 'PARTIAL'">Partial</small>
            </td>
            <td class="trend-cell">
              <svg viewBox="0 0 110 30" preserveAspectRatio="none">
                <polyline [attr.points]="sparkline(metric)"></polyline>
              </svg>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </details>

  <app-page-data-panel *ngIf="report?.dataQualityIssues?.length"
    title="Data Quality" subtitle="Incomplete inputs that prevent a metric from being treated as authoritative."
    [resultText]="(report?.dataQualityIssues?.length || 0) + ' issues'">
    <div class="quality-list">
      <article *ngFor="let issue of report?.dataQualityIssues">
        <p-tag [value]="issue.severity" [severity]="statusSeverity(issue.severity === 'HIGH' ? 'RED' : 'AMBER')"></p-tag>
        <div>
          <strong>{{ issue.companyName }} · {{ monthNames[(issue.month || 1) - 1] }} · {{ issue.metricCode }}</strong>
          <span>{{ issue.description }}</span>
        </div>
      </article>
    </div>
  </app-page-data-panel>

  <app-hr-action-dialog [visible]="activeDialog === 'generate'"
    (visibleChange)="activeDialog = $event ? 'generate' : undefined"
    title="Generate Reporting Month" eyebrow="Governed Snapshot"
    subtitle="Recalculate a draft month for the selected company scope. Locked periods cannot be regenerated."
    icon="fa-solid fa-rotate" [contextTitle]="monthNames[generationMonth - 1] + ' ' + year">
    <div class="dialog-form">
      <p-inputGroup>
        <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
        <p-iftalabel>
          <p-select inputId="generationMonth" [options]="monthOptions" optionLabel="label" optionValue="value"
            [(ngModel)]="generationMonth"></p-select>
          <label for="generationMonth">Reporting month</label>
        </p-iftalabel>
      </p-inputGroup>
      <button pButton type="button" label="Generate Snapshots" icon="fa-solid fa-rotate" (click)="generate()"></button>
    </div>
  </app-hr-action-dialog>

  <app-hr-action-dialog [visible]="activeDialog === 'details'"
    (visibleChange)="activeDialog = $event ? 'details' : undefined"
    title="Metric Evidence" eyebrow="Calculation Detail"
    [subtitle]="selectedMetric?.metricName || ''" icon="fa-solid fa-magnifying-glass-chart"
    [contextTitle]="selectedCompany?.companyName || ''"
    [contextSubtitle]="monthNames[(selectedMonth?.month || 1) - 1] + ' ' + year">
    <div class="metric-detail" *ngIf="metricDetails; else detailsLoading">
      <div><span>Value</span><strong>{{ selectedMonth?.displayValue }}</strong></div>
      <div><span>Scorecard</span><p-tag [value]="selectedMonth?.scorecardStatus"
        [severity]="statusSeverity(selectedMonth?.scorecardStatus)"></p-tag></div>
      <div><span>Data quality</span><p-tag [value]="metricDetails.dataQualityStatus"
        [severity]="statusSeverity(metricDetails.dataQualityStatus)"></p-tag></div>
      <div><span>Source records</span><strong>{{ metricDetails.sourceCount || 0 }}</strong></div>
      <section>
        <h3>Definition</h3>
        <p>{{ metricDetails.calculationDescription }}</p>
      </section>
      <section>
        <h3>Assumptions and evidence</h3>
        <pre>{{ metricDetails.assumptionsJson || 'No additional assumptions.' }}</pre>
      </section>
      <section class="source-evidence" *ngIf="metricDetails.sourceRecords?.length">
        <h3>Source records</h3>
        <div class="evidence-table-wrap">
          <table class="evidence-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Record</th>
                <th>Date</th>
                <th>Status</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let record of metricDetails.sourceRecords">
                <td>{{ record.sourceType || '-' }}</td>
                <td>{{ record.label || record.sourceId || '-' }}</td>
                <td>{{ record.recordDate ? (record.recordDate | date:'mediumDate') : '-' }}</td>
                <td>{{ record.status || '-' }}</td>
                <td>{{ record.value || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      <button *ngIf="canOverride" pButton type="button" label="Override Status"
        icon="fa-solid fa-pen" severity="secondary" [outlined]="true" (click)="openOverride()"></button>
    </div>
    <ng-template #detailsLoading><p class="muted">Loading calculation evidence…</p></ng-template>
  </app-hr-action-dialog>

  <app-hr-action-dialog [visible]="activeDialog === 'reopen'"
    (visibleChange)="activeDialog = $event ? 'reopen' : undefined"
    title="Reopen Locked Month" eyebrow="Controlled Exception"
    subtitle="Reopening permits recalculation and records the actor, time, and reason."
    icon="fa-solid fa-lock-open">
    <div class="dialog-form">
      <p-inputGroup>
        <p-inputGroupAddon><i class="fa-solid fa-message"></i></p-inputGroupAddon>
        <p-iftalabel>
          <textarea pTextarea id="reopenReason" rows="4" [(ngModel)]="reopenReason"></textarea>
          <label for="reopenReason">Required reopen reason</label>
        </p-iftalabel>
      </p-inputGroup>
      <button pButton type="button" label="Reopen Period" icon="fa-solid fa-lock-open"
        severity="warn" [disabled]="!reopenReason.trim()" (click)="reopen()"></button>
    </div>
  </app-hr-action-dialog>

  <app-hr-action-dialog [visible]="activeDialog === 'scorecards'"
    (visibleChange)="activeDialog = $event ? 'scorecards' : undefined"
    title="Scorecard Rules" eyebrow="Tenant Configuration"
    subtitle="Configure company-specific green and amber thresholds."
    icon="fa-solid fa-gauge-high">
    <div class="scorecard-list">
      <article *ngFor="let rule of scorecards">
        <div><strong>{{ rule.metricCode }}</strong><small>{{ rule.direction?.replaceAll('_', ' ') }}</small></div>
        <p-inputNumber [(ngModel)]="rule.greenThreshold" placeholder="Green"></p-inputNumber>
        <p-inputNumber [(ngModel)]="rule.amberThreshold" placeholder="Amber"></p-inputNumber>
        <button pButton type="button" icon="fa-solid fa-save" [text]="true" (click)="saveScorecard(rule)"></button>
      </article>
    </div>
  </app-hr-action-dialog>

  <app-hr-action-dialog [visible]="activeDialog === 'override'"
    (visibleChange)="activeDialog = $event ? 'override' : undefined"
    title="Override Scorecard Status" eyebrow="Audited Decision"
    subtitle="The calculated value remains unchanged. Only the displayed status is overridden."
    icon="fa-solid fa-pen">
    <div class="dialog-form">
      <p-inputGroup>
        <p-inputGroupAddon><i class="fa-solid fa-traffic-light"></i></p-inputGroupAddon>
        <p-iftalabel>
          <p-select inputId="overrideStatus" [options]="statusOptions" optionLabel="label" optionValue="value"
            [(ngModel)]="overrideStatus"></p-select>
          <label for="overrideStatus">Override status</label>
        </p-iftalabel>
      </p-inputGroup>
      <p-inputGroup>
        <p-inputGroupAddon><i class="fa-solid fa-message"></i></p-inputGroupAddon>
        <p-iftalabel>
          <textarea pTextarea id="overrideReason" rows="4" [(ngModel)]="overrideReason"></textarea>
          <label for="overrideReason">Required reason</label>
        </p-iftalabel>
      </p-inputGroup>
      <button pButton type="button" label="Save Audited Override" icon="fa-solid fa-save"
        [disabled]="!overrideReason.trim()" (click)="saveOverride()"></button>
    </div>
  </app-hr-action-dialog>
</app-page-shell>
